import { TabCompletionStatus } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-tab";

type CompletionStatusHolder = { completionStatus?: TabCompletionStatus };

/** Status as the form computed it, before the review-specific colour mapping. */
const rawStatusByTab = new WeakMap<object, TabCompletionStatus | undefined>();

const frozenStatusByTab = new WeakMap<
  object,
  TabCompletionStatus | undefined
>();

/** Per-tab check for "does this tab have an edit not yet persisted by a save?" */
const isDirtyByTab = new WeakMap<object, () => boolean>();

/** Per-tab check for "has the reviewer opened this block?" */
const isVisitedByTab = new WeakMap<object, () => boolean>();

export function getRawCompletionStatus(
  tab: CompletionStatusHolder
): TabCompletionStatus | undefined {
  return rawStatusByTab.has(tab)
    ? rawStatusByTab.get(tab)
    : tab.completionStatus;
}

/**
 * Status to show the user: the live status while the tab has no unsaved
 * edit, otherwise whatever it was before that edit started. `reset()` after
 * a successful save clears the underlying dirty state, so this naturally
 * starts tracking the live (now-saved) status again on the next render,
 * with no separate "accept the save" step needed.
 *
 * A complete block the reviewer has not opened yet shows as "in-progress",
 * so prefilled (imported or LLM-suggested) answers do not read as reviewed.
 */
export function getDisplayCompletionStatus(
  tab: CompletionStatusHolder
): TabCompletionStatus | undefined {
  const raw = getRawCompletionStatus(tab);
  if (!raw) return raw;
  let status: TabCompletionStatus;
  if (isDirtyByTab.get(tab)?.() ?? false) {
    status = frozenStatusByTab.get(tab) ?? "not-started";
  } else {
    frozenStatusByTab.set(tab, raw);
    status = raw;
  }
  const isVisited = isVisitedByTab.get(tab)?.() ?? true;
  return status === "complete" && !isVisited ? "in-progress" : status;
}

/**
 * Maps the form's own status onto the sidebar's `info.main` colour (purple in
 * this theme) for `in-progress`, so a submitted review can go all-purple
 * without a "submitted" status existing in pimo-components. Shadows the tab's
 * accessor rather than the private field, so the form keeps writing the real
 * status; read it back with `getRawCompletionStatus`/`getDisplayCompletionStatus`.
 */
export function applyReviewBlockColors(
  tab: CompletionStatusHolder,
  isSubmitted: () => boolean,
  isDirty: () => boolean = () => false,
  isVisited: () => boolean = () => true
): void {
  rawStatusByTab.set(tab, tab.completionStatus);
  isDirtyByTab.set(tab, isDirty);
  isVisitedByTab.set(tab, isVisited);

  Object.defineProperty(tab, "completionStatus", {
    configurable: true,
    get: () => {
      const raw = rawStatusByTab.get(tab);
      if (!raw) return raw;
      if (isSubmitted()) return "in-progress";
      const displayed = getDisplayCompletionStatus(tab);
      return displayed === "complete" ? "complete" : "not-started";
    },
    set: (value: TabCompletionStatus | undefined) =>
      rawStatusByTab.set(tab, value),
  });
}
